import React, { useState } from "react";
import { Phone, AlertTriangle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GuideActions } from "@/components/GuideActions";
import { useLang } from "@/lib/lang";
import { T } from "@/lib/i18n";

const rich = (t) => {
  const bold = t.startsWith("**") && t.endsWith("**");
  return bold ? <strong className="text-bsn-midnight font-semibold">{t.slice(2, -2)}</strong> : t;
};

const CrisisLine = ({ emergency }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {emergency && <a href="tel:911" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-700 text-white text-sm font-semibold"><Phone className="w-4 h-4" /> Call 911</a>}
    <a href="tel:988" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-bsn-midnight text-bsn-parchment text-sm font-semibold"><Phone className="w-4 h-4" /> Call 988</a>
    <a href="sms:988" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-bsn-midnight/20 text-bsn-midnight text-sm font-semibold">Text 988</a>
    <a href="https://988lifeline.org/chat/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-bsn-midnight/20 text-bsn-midnight text-sm font-semibold">Chat online</a>
  </div>
);

const Section = ({ s }) => (
  <div className={s.alert ? "rounded-xl bg-red-50 border border-red-200 p-5" : ""}>
    {s.h && (
      <h4 className={`font-serif text-xl font-semibold mb-2 flex items-center gap-2 ${s.alert ? "text-red-800" : "text-bsn-midnight"}`}>
        {s.alert && <AlertTriangle className="w-5 h-5" />}{s.h}
      </h4>
    )}
    {s.quote && <p className="font-serif italic text-lg text-bsn-bronze border-l-2 border-bsn-gold pl-4 my-3">{s.quote}</p>}
    {s.p?.map((t, i) => <p key={i} className="mt-2 first:mt-0">{rich(t)}</p>)}
    {s.list && (
      <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {s.list.map((t) => <li key={t} className="flex gap-2"><span className="text-bsn-gold">•</span>{t}</li>)}
      </ul>
    )}
    {s.levels && (
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {s.levels.map((l, i) => (
          <div key={l.t} className="rounded-xl bg-bsn-canvas border border-bsn-gold/25 p-4">
            <p className="font-semibold text-bsn-midnight">{l.t} <span className="text-bsn-gold">{"●".repeat(i + 1)}</span></p>
            <p className="text-sm mt-1.5">{l.d}</p>
          </div>
        ))}
      </div>
    )}
    {s.after?.map((t, i) => <p key={i} className="mt-3">{rich(t)}</p>)}
    {s.alert && <CrisisLine emergency={s.emergency} />}
    {s.alert && !s.emergency && <p className="mt-3 text-sm text-red-800">If someone is in immediate danger or has already harmed themselves, call 911.</p>}
  </div>
);

export const ArticleBody = ({ article }) => (
  <div className="space-y-6 text-bsn-sapphire/85 leading-relaxed" data-testid={`article-body-${article.id}`}>
    {article.sections.map((s, i) => <Section key={i} s={s} />)}
    <p className="text-xs text-bsn-sapphire/60 italic pt-2 border-t border-bsn-gold/20">{article.note}</p>
  </div>
);

// Full article: big title, clickable table of contents, and each heading as its own dropdown.
export const Article = ({ article, index, eyebrow, path }) => {
  const [open, setOpen] = useState([]);
  const t = T[useLang().lang];
  const intro = article.sections.filter((s) => !s.h);
  const parts = article.sections.filter((s) => s.h);
  const jump = (k) => {
    setOpen((o) => (o.includes(k) ? o : [...o, k]));
    setTimeout(() => document.getElementById(`${article.id}-${k}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  return (
    <article className="bg-white rounded-3xl card-tactile border-t-4 border-bsn-gold overflow-hidden" data-testid={`article-${article.id}`}>
      <div className="p-7 sm:p-10">
        <p className="eyebrow mb-2">{eyebrow || `${t.eyebrow} · ${index + 1}`}</p>
        <h3 className="font-serif text-3xl sm:text-4xl font-semibold text-bsn-midnight leading-tight">{article.title}</h3>
        <p className="mt-3 text-lg text-bsn-sapphire/75">{article.summary}</p>
        <div className="mt-5"><GuideActions article={article} path={path || `/resources#guide-${article.id}`} /></div>
        <div className="mt-5 space-y-3 text-bsn-sapphire/85 leading-relaxed">{intro.map((s, i) => <Section key={i} s={s} />)}</div>

        <nav className="mt-8 rounded-2xl bg-bsn-canvas border border-bsn-gold/30 p-5" aria-label={`${article.title} contents`} data-testid={`article-toc-${article.id}`}>
          <p className="font-mono text-xs uppercase tracking-widest text-bsn-bronze mb-3">{t.toc}</p>
          <ol className="grid sm:grid-cols-2 gap-2">
            {parts.map((s, k) => (
              <li key={k}>
                <button onClick={() => jump(`s${k}`)} className={`w-full text-left flex items-center gap-3 px-4 py-2.5 rounded-xl font-semibold text-sm border ${s.alert ? "bg-red-50 border-red-200 text-red-800 hover:bg-red-100" : "bg-white border-bsn-gold/30 text-bsn-midnight hover:border-bsn-gold"}`} data-testid={`toc-${article.id}-${k}`}>
                  <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center shrink-0 ${s.alert ? "bg-red-700 text-white" : "bg-bsn-midnight text-bsn-gold"}`}>{k + 1}</span>
                  {s.h.replace(/^\d+\.\s*/, "")}
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      <Accordion type="multiple" value={open} onValueChange={setOpen} className="border-t border-bsn-gold/20">
        {parts.map((s, k) => (
          <AccordionItem key={k} value={`s${k}`} id={`${article.id}-s${k}`} className="scroll-mt-24 border-b border-bsn-gold/15 px-7 sm:px-10" data-testid={`section-${article.id}-${k}`}>
            <AccordionTrigger className={`hover:no-underline text-left font-serif text-xl sm:text-2xl font-semibold py-5 ${s.alert ? "text-red-800" : "text-bsn-midnight"}`}>
              <span className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full text-sm font-sans flex items-center justify-center shrink-0 ${s.alert ? "bg-red-700 text-white" : "bg-bsn-gold/20 text-bsn-bronze"}`}>{k + 1}</span>
                {s.h.replace(/^\d+\.\s*/, "")}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 text-base text-bsn-sapphire/85 leading-relaxed">
              <Section s={{ ...s, h: null }} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="px-7 sm:px-10 py-5 text-xs text-bsn-sapphire/60 italic bg-bsn-canvas/60 space-y-1">
        {article.note !== t.disclaimer && <p>{article.note}</p>}
        <p data-testid={`article-disclaimer-${article.id}`}>{t.disclaimer}</p>
      </div>
    </article>
  );
};
